import EmptyState from '@/components/EmptyState';
import { useReservationList } from '@/features/reservations/hooks/useReservations';
import { useTables, useZones } from '@/features/reservation-setup/hooks/useReservationSetup';
import { useT } from '@/i18n';
import { fromApi, fromDateString } from '@/utils/datetime';
import { getDisplayStatus, getStatusColor } from '@/utils/status';
import type { Reservation, RestaurantTable, Zone } from '@restaurant/api-client';
import { Block, Preloader } from 'framework7-react';
import { DateTime } from 'luxon';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// ─── Layout constants ─────────────────────────────────────────────────────────

const HOUR_WIDTH = 90; // px per hour on the time axis - the MINIMUM (see hourWidth)
const ROW_HEIGHT = 36; // px per table row (tighter - ordinal-only labels)
const HEADER_HEIGHT = 28; // sticky time-ruler
const ZONE_HEADER_HEIGHT = 24; // sticky per-zone divider
const SEAT_HEADER_HEIGHT = 22; // sticky per-seat-size sub-divider
// Gutter sizing - measured per render. Stays inside [MIN, MAX] to avoid both
// a too-narrow column on a "T1" / "T2" setup and a runaway column when a user
// names a manual table something verbose.
// Gutter sizing. Must match `padding` on `.rsv-timeline-rowlabel` exactly -
// any drift between measurement and CSS shows up as ellipsis-truncated labels
// that visually had room to spare (or vice versa).
const GUTTER_PAD_X = 12; // 6 + 6
const MIN_GUTTER_W = 40;
const MAX_GUTTER_W = 160;
// Always render the full day (00:00 → 24:00) so a booking can never be clipped
// off either end of the axis. The view auto-scrolls to "now" / the first booking
// on open, so the empty hours stay out of the way without hiding anything.
const DAY_START_HOUR = 0;
const DAY_END_HOUR = 24;

const UNASSIGNED_KEY = '__unassigned__';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Per-row label - kept minimal because zone and seat count are already
 *  shown by their sticky group headers above. For SIMPLE auto-generated
 *  tables that's just the ordinal (`#3`); for manual ADVANCED-mode tables
 *  it's the user-chosen name (`T1`, `Bar-3`). */
function tableLabel(t: RestaurantTable): string {
  if (t.ordinal != null) return `#${t.ordinal}`;
  return t.label && t.label.trim() !== '' ? t.label : '-';
}

interface TimelineRow {
  key: string;
  /** Display label rendered in the sticky left gutter - just the ordinal
   *  ("#3") or the manual name ("T1"). Seat count is on the sub-header. */
  label: string;
  /** Reservations on this row. */
  bookings: Reservation[];
}

interface SeatGroup {
  key: string;
  /** Seat count for the sticky sub-header. `null` skips the sub-header
   *  (used for the synthetic Unassigned section which has only one row). */
  seats: number | null;
  rows: TimelineRow[];
}

interface TimelineSection {
  key: string;
  label: string;
  groups: SeatGroup[];
  /** True for the synthetic "Unassigned" section pinned at the top. */
  pinned?: boolean;
}

/** Pack overlapping bookings into separate lanes so they never visually overlap.
 *  Greedy first-fit: walk in start order and drop each booking into the first
 *  lane whose last booking ends at or before this one starts; otherwise open a
 *  new lane. Yields the minimum number of lanes (= peak concurrency). Used for
 *  the synthetic "Unassigned" section, where many bookings share no table row. */
function packIntoLanes(bookings: Reservation[]): Reservation[][] {
  const sorted = [...bookings].sort(
    (a, b) => fromApi(a.time).toMillis() - fromApi(b.time).toMillis(),
  );
  const lanes: { end: number; items: Reservation[] }[] = [];
  for (const r of sorted) {
    const start = fromApi(r.time).toMillis();
    const end = fromApi(r.time).plus({ minutes: r.duration }).toMillis();
    const lane = lanes.find((l) => l.end <= start);
    if (lane) {
      lane.end = end;
      lane.items.push(r);
    } else {
      lanes.push({ end, items: [r] });
    }
  }
  return lanes.map((l) => l.items);
}

/** Build the sections + rows. Tables group by zone (sortOrder, then label).
 *  An "Unassigned" section is prepended whenever any booking has empty
 *  `tableIds` - that's the common case in SIMPLE mode and for not-yet-
 *  seated bookings in ADVANCED. Zones with no active bookable tables are
 *  omitted entirely. */
function buildSections(
  reservations: Reservation[],
  tables: RestaurantTable[],
  zones: Zone[],
  labels: { unzoned: string; unassignedCount: (n: number) => string },
): TimelineSection[] {
  const zonesById = new Map(zones.map((z) => [z.id, z]));
  const byTable = new Map<string, Reservation[]>();
  const unassigned: Reservation[] = [];
  for (const r of reservations) {
    if (r.tableIds.length === 0) {
      unassigned.push(r);
      continue;
    }
    for (const tableId of r.tableIds) {
      const arr = byTable.get(tableId) ?? [];
      arr.push(r);
      byTable.set(tableId, arr);
    }
  }

  // Group tables → zone bucket → seat-size sub-bucket. `null` zone (orphan
  // tables) gets its own "Unzoned" bucket so they're still visible.
  const tablesByZoneKey = new Map<string, RestaurantTable[]>();
  for (const t of tables) {
    if (!t.isActive || !t.countsForCapacity) continue;
    const key = t.zoneId ?? '__nozone__';
    const arr = tablesByZoneKey.get(key) ?? [];
    arr.push(t);
    tablesByZoneKey.set(key, arr);
  }

  const zoneSections: TimelineSection[] = [];
  for (const [key, ts] of tablesByZoneKey) {
    const zone = key === '__nozone__' ? null : (zonesById.get(key) ?? null);

    // Sub-bucket the tables in this zone by seat count, sorted ascending -
    // smaller tables first reads naturally to staff (2-tops, then 4-tops…).
    const bySeats = new Map<number, RestaurantTable[]>();
    for (const t of ts) {
      const arr = bySeats.get(t.seats) ?? [];
      arr.push(t);
      bySeats.set(t.seats, arr);
    }
    const groups: SeatGroup[] = [...bySeats.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([seats, items]) => ({
        key: `${key}-${seats}`,
        seats,
        rows: items
          .map((t) => ({
            key: t.id,
            label: tableLabel(t),
            bookings: byTable.get(t.id) ?? [],
          }))
          // Sort within a seat bucket: ordinal asc when present, label fallback.
          .sort((a, b) => {
            const oa = items.find((x) => x.id === a.key)?.ordinal;
            const ob = items.find((x) => x.id === b.key)?.ordinal;
            if (oa != null && ob != null) return oa - ob;
            return a.label.localeCompare(b.label);
          }),
      }));

    zoneSections.push({
      key: zone?.id ?? '__nozone__',
      label: zone?.label ?? labels.unzoned,
      groups,
    });
  }
  // Sort zones by sortOrder (Unzoned drops to the bottom).
  zoneSections.sort((a, b) => {
    const sa = zonesById.get(a.key)?.sortOrder ?? Number.POSITIVE_INFINITY;
    const sb = zonesById.get(b.key)?.sortOrder ?? Number.POSITIVE_INFINITY;
    return sa - sb || a.label.localeCompare(b.label);
  });

  const sections: TimelineSection[] = [];
  if (unassigned.length > 0) {
    // Lane-pack so concurrent unassigned bookings stack into separate rows
    // instead of overlapping on one. The section header carries the total; each
    // lane row stays label-less (the rows are an internal packing detail, not
    // named tables).
    const lanes = packIntoLanes(unassigned);
    sections.push({
      key: UNASSIGNED_KEY,
      label: labels.unassignedCount(unassigned.length),
      pinned: true,
      // `seats: null` tells the renderer to skip the seat sub-header.
      groups: [
        {
          key: `${UNASSIGNED_KEY}-row`,
          seats: null,
          rows: lanes.map((items, i) => ({
            key: `${UNASSIGNED_KEY}-lane-${i}`,
            label: '',
            bookings: items,
          })),
        },
      ],
    });
  }
  sections.push(...zoneSections);
  return sections;
}

// ─── Gutter width measurement ─────────────────────────────────────────────────
//
// One shared canvas for all `measureText` calls - avoids creating + GC'ing a
// canvas per render. Lazily initialised on first use (SSR-safe).
let _canvas: HTMLCanvasElement | null = null;
function measureText(text: string, font: string): number {
  if (typeof document === 'undefined') return 0;
  if (!_canvas) _canvas = document.createElement('canvas');
  const ctx = _canvas.getContext('2d');
  if (!ctx) return 0;
  ctx.font = font;
  return ctx.measureText(text).width;
}

/** Compute the gutter width that just fits every row label + section header
 *  in the timeline. Both lines (main + meta) are measured because either can
 *  be the longest. Result is clamped to [MIN_GUTTER_W, MAX_GUTTER_W] - beyond
 *  that we let labels truncate with ellipsis rather than steal more of the
 *  time canvas. */
function computeGutterWidth(sections: TimelineSection[]): number {
  // Fonts must match the corresponding CSS rules (`.rsv-timeline-rowlabel-main`,
  // `-zone-label`, `-seat-label`). Drift between these strings and CSS shows
  // up as labels being clipped (or the gutter being wider than needed).
  const mainFont = '600 12px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  const zoneFont = '700 11px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  const seatFont = '600 11px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  let widest = 0;
  for (const s of sections) {
    // Zone labels are uppercased AND letter-spaced (0.04em) in CSS. canvas
    // measureText ignores letter-spacing, so add it back per character - else a
    // long section header ("CHƯA XẾP BÀN · 1") measures ~7px short, the gutter
    // comes up narrow, and the nowrap label clips against the zone divider.
    const zoneLabel = s.label.toUpperCase();
    const zoneLetterSpacing = zoneLabel.length * 11 * 0.04;
    widest = Math.max(widest, measureText(zoneLabel, zoneFont) + zoneLetterSpacing);
    for (const g of s.groups) {
      if (g.seats != null) {
        widest = Math.max(widest, measureText(`${g.seats}-top`, seatFont));
      }
      for (const r of g.rows) {
        widest = Math.max(widest, measureText(r.label, mainFont));
      }
    }
  }
  return Math.min(MAX_GUTTER_W, Math.max(MIN_GUTTER_W, Math.ceil(widest) + GUTTER_PAD_X));
}

// ─── View ─────────────────────────────────────────────────────────────────────

/**
 * Tables (Y) × time (X), grouped by zone. Each zone gets a sticky iOS-style
 * section header; inside it, one row per active bookable table.
 *
 * Layout = vertical stack of flex rows, each spanning the full content width
 * (gutterWidth + canvasWidth). The left-column cells use `position: sticky;
 * left: 0` - placing them inside a full-width row means sticky works across
 * the entire horizontal scroll range. (Earlier CSS-grid version stuck the
 * left column inside a 160 px column track, so it lost stickiness after the
 * first 160 px of horizontal scroll - the bug the user reported.)
 */
export default function TimelineView({
  date,
  selectedId,
  onSelectReservation,
}: {
  date: string;
  selectedId: string | null;
  onSelectReservation: (reservation: Reservation) => void;
}) {
  const t = useT();
  const { data, isLoading, error } = useReservationList(date);
  const { data: tables } = useTables(true);
  const { data: zones } = useZones();

  const reservations = useMemo(() => data?.data ?? [], [data]);
  const sections = useMemo(
    () => buildSections(reservations, tables ?? [], zones ?? [], t.reservations.timeline),
    [reservations, tables, zones, t.reservations.timeline],
  );

  const startHour = DAY_START_HOUR;
  const endHour = DAY_END_HOUR;
  const hours = endHour - startHour;
  const gutterWidth = useMemo(() => computeGutterWidth(sections), [sections]);

  // Measure the scrollport so the hour columns can STRETCH to fill it when the
  // natural width (hours × HOUR_WIDTH) is narrower than the pane - otherwise a
  // wide desktop pane leaves a large empty strip to the right of the canvas.
  // HOUR_WIDTH stays the floor, so on a narrow pane the timeline still scrolls.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => setViewportWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const hourWidth =
    hours > 0
      ? Math.max(HOUR_WIDTH, Math.floor((viewportWidth - gutterWidth) / hours))
      : HOUR_WIDTH;
  const canvasWidth = hours * hourWidth;

  // "Now" indicator - only meaningful if we're viewing today and `now` falls
  // inside the visible range.
  const day = fromDateString(date);
  const now = DateTime.now();
  const isToday = day.hasSame(now, 'day');
  const nowOffset = isToday ? (now.hour + now.minute / 60 - startHour) * hourWidth : null;

  // Auto-scroll horizontally to bring "now" (or the earliest booking) into
  // view on first render / date change.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let target = 0;
    if (nowOffset != null) {
      target = Math.max(0, nowOffset - el.clientWidth / 3);
    } else if (reservations.length > 0) {
      const first = reservations
        .map((r) => fromApi(r.time))
        .sort((a, b) => a.toMillis() - b.toMillis())[0];
      target = Math.max(0, (first.hour + first.minute / 60 - startHour) * hourWidth - 80);
    }
    el.scrollLeft = target;
    // Only on date change / first reservations arrival - deliberately not
    // re-scrolling as individual reservations change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, reservations.length === 0]);

  if (isLoading) {
    return (
      <Block style={{ textAlign: 'center', paddingTop: 48 }}>
        <Preloader size={32} />
      </Block>
    );
  }
  if (error) {
    return (
      <EmptyState
        icon="exclamationmark_triangle"
        title={t.reservations.empty.errorTitle}
        description={t.reservations.empty.errorDesc}
      />
    );
  }
  if (sections.length === 0) {
    return (
      <EmptyState
        icon="chart_bar"
        title={t.reservations.empty.timelineTitle}
        description={t.reservations.empty.timelineDesc}
      />
    );
  }

  const contentWidth = gutterWidth + canvasWidth;
  const totalBodyHeight = sections.reduce(
    (acc, s) =>
      acc +
      ZONE_HEADER_HEIGHT +
      s.groups.reduce(
        (g, group) =>
          g + (group.seats != null ? SEAT_HEADER_HEIGHT : 0) + group.rows.length * ROW_HEIGHT,
        0,
      ),
    0,
  );

  // One source of truth for every cell width. The CSS variables avoid passing
  // numbers individually to corner / rowlabel / zone-label cells (which is
  // where drift / off-by-one bugs creep in). All cell widths in CSS read from
  // these variables - they cannot disagree.
  const tlVars = {
    ['--rsv-tl-gutter' as string]: `${gutterWidth}px`,
    ['--rsv-tl-canvas' as string]: `${canvasWidth}px`,
    ['--rsv-tl-content' as string]: `${contentWidth}px`,
    ['--rsv-tl-row' as string]: `${ROW_HEIGHT}px`,
    ['--rsv-tl-header' as string]: `${HEADER_HEIGHT}px`,
    ['--rsv-tl-zone' as string]: `${ZONE_HEADER_HEIGHT}px`,
    ['--rsv-tl-seat' as string]: `${SEAT_HEADER_HEIGHT}px`,
  } as React.CSSProperties;

  return (
    <div className="rsv-timeline" ref={scrollRef} style={tlVars}>
      <div className="rsv-timeline-body">
        {/* Sticky top header: corner + hour ruler. */}
        <div className="rsv-timeline-header">
          <div className="rsv-timeline-corner" />
          <div className="rsv-timeline-ruler">
            {/* Ruler tick marks - match the body gridlines exactly so the eye
                can confirm columns line up top-to-bottom. Both originate from
                the same `i * HOUR_WIDTH` math, both live inside elements that
                are `canvasWidth` wide and start at `gutterWidth` from the
                viewport-left. */}
            {Array.from({ length: hours + 1 }, (_, i) => (
              <div
                key={`tick-${i}`}
                className="rsv-timeline-tick"
                style={{ left: i * hourWidth }}
              />
            ))}
            {/* Hour labels - anchored to the same gridline as their tick.
                The last tick (i = hours) gets no label because the text would
                clip past canvasWidth. */}
            {Array.from({ length: hours }, (_, i) => {
              const h = startHour + i;
              return (
                <span key={i} className="rsv-timeline-hour" style={{ left: i * hourWidth }}>
                  {h.toString().padStart(2, '0')}:00
                </span>
              );
            })}
          </div>
        </div>

        {/* Zone sections */}
        {sections.map((section) => (
          <SectionItem
            key={section.key}
            section={section}
            startHour={startHour}
            canvasWidth={canvasWidth}
            hourWidth={hourWidth}
            selectedId={selectedId}
            onSelectReservation={onSelectReservation}
          />
        ))}

        {/* "Now" vertical line - spans the body, sits in the time canvas */}
        {nowOffset != null && nowOffset >= 0 && nowOffset <= canvasWidth && (
          <div
            className="rsv-timeline-now"
            style={{
              left: gutterWidth + nowOffset,
              top: HEADER_HEIGHT,
              height: totalBodyHeight,
            }}
          />
        )}
      </div>
    </div>
  );
}

function SectionItem({
  section,
  startHour,
  canvasWidth,
  hourWidth,
  selectedId,
  onSelectReservation,
}: {
  section: TimelineSection;
  startHour: number;
  canvasWidth: number;
  hourWidth: number;
  selectedId: string | null;
  onSelectReservation: (r: Reservation) => void;
}) {
  return (
    <>
      <div className={`rsv-timeline-zone${section.pinned ? ' rsv-timeline-zone-pinned' : ''}`}>
        <div className="rsv-timeline-zone-label">{section.label}</div>
        <div className="rsv-timeline-zone-spacer" />
      </div>

      {section.groups.map((group) => (
        <SeatGroupItem
          key={group.key}
          group={group}
          startHour={startHour}
          canvasWidth={canvasWidth}
          hourWidth={hourWidth}
          selectedId={selectedId}
          onSelectReservation={onSelectReservation}
        />
      ))}
    </>
  );
}

function SeatGroupItem({
  group,
  startHour,
  canvasWidth,
  hourWidth,
  selectedId,
  onSelectReservation,
}: {
  group: SeatGroup;
  startHour: number;
  canvasWidth: number;
  hourWidth: number;
  selectedId: string | null;
  onSelectReservation: (r: Reservation) => void;
}) {
  return (
    <>
      {/* Skip the seat-size sub-header for the synthetic Unassigned row. */}
      {group.seats != null && (
        <div className="rsv-timeline-seat">
          <div className="rsv-timeline-seat-label">{group.seats}-top</div>
          <div className="rsv-timeline-seat-spacer" />
        </div>
      )}
      {group.rows.map((row) => (
        <RowItem
          key={row.key}
          row={row}
          startHour={startHour}
          canvasWidth={canvasWidth}
          hourWidth={hourWidth}
          selectedId={selectedId}
          onSelectReservation={onSelectReservation}
        />
      ))}
    </>
  );
}

function RowItem({
  row,
  startHour,
  canvasWidth,
  hourWidth,
  selectedId,
  onSelectReservation,
}: {
  row: TimelineRow;
  startHour: number;
  canvasWidth: number;
  hourWidth: number;
  selectedId: string | null;
  onSelectReservation: (r: Reservation) => void;
}) {
  return (
    <div className="rsv-timeline-row">
      <div className="rsv-timeline-rowlabel">
        <span className="rsv-timeline-rowlabel-main">{row.label}</span>
      </div>

      <div className="rsv-timeline-rowcanvas">
        {Array.from({ length: Math.ceil(canvasWidth / hourWidth) + 1 }, (_, i) => (
          <div key={i} className="rsv-timeline-gridline" style={{ left: i * hourWidth }} />
        ))}
        {row.bookings.map((r) => (
          <TimelineBar
            key={`${row.key}-${r.id}`}
            reservation={r}
            startHour={startHour}
            hourWidth={hourWidth}
            selected={r.id === selectedId}
            onSelect={() => onSelectReservation(r)}
          />
        ))}
      </div>
    </div>
  );
}

function TimelineBar({
  reservation,
  startHour,
  hourWidth,
  selected,
  onSelect,
}: {
  reservation: Reservation;
  startHour: number;
  hourWidth: number;
  selected: boolean;
  onSelect: () => void;
}) {
  const start = fromApi(reservation.time);
  const end = start.plus({ minutes: reservation.duration });
  const left = (start.hour + start.minute / 60 - startHour) * hourWidth;
  const width = Math.max(24, ((end.toMillis() - start.toMillis()) / (1000 * 60 * 60)) * hourWidth);
  const color = getStatusColor(getDisplayStatus(reservation.status, reservation.arrivalStatus));

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`rsv-timeline-bar${selected ? ' rsv-timeline-bar-selected' : ''}`}
      // Time is on the axis above - repeating it inside the bar created an
      // unavoidable visual desync (bar text inset by border+padding vs ruler
      // label at the gridline). Tooltip + the bar's leading edge already
      // communicate it.
      title={`${reservation.guest.name} · ${start.toFormat('HH:mm')}–${end.toFormat('HH:mm')} · ${reservation.partySize}p`}
      style={{
        left,
        width,
        background: selected ? `rgba(var(--f7-theme-color-rgb), 0.18)` : `${color}1a`,
        // Tinted left border supplies the status color without forcing inner
        // text to inset - see CSS for the matching `border-left-color`.
        borderLeftColor: color,
      }}
    >
      <span className="rsv-timeline-bar-name">{reservation.guest.name}</span>
      <span className="rsv-timeline-bar-meta">{reservation.partySize}p</span>
    </button>
  );
}
